import { Viewport } from '@/enums/viewport';
import { PAGINATION_LEVELS, PaginationLevel } from '@/stores/pinia/useMetaStore';
import { useResizeObserver, useScroll } from '@vueuse/core';
import { Ref, computed, onActivated, onDeactivated, onMounted, readonly, ref, watch } from 'vue';
import { Route } from 'vue-router';
import { useRoute, useRouter } from 'vue-router/composables';
import { getViewport } from '../ui-helper';
import { parsePageNumber } from './usePagination';

export interface LoadMorePayload {
	event: ReturnType<typeof createLoadMoreEvent>;
	page?: number | undefined;
}

const ANCHOR_NAME = 'jw_page_anchor' as const;
export const BI_SCROLL_TOP_OFFSET_PLACEMENT_ID = 'jw_page_top_offset_placement' as const;

function getPageQueryParam(routeQuery: Route['query']) {
	const page = (routeQuery.page as unknown as string) || '1';
	return parsePageNumber(page);
}

function getThresholdValue(viewport: Viewport): number {
	const { innerHeight } = window;
	switch (viewport) {
		case 'tablet':
			return innerHeight * 1.5;
		case 'smartphone':
			return innerHeight * 2.6;
		default:
			return innerHeight * 0.8;
	}
}

// we don't need to create a full blown Event.
// we only need to support `event.target.complete()` calls, that are called after a loadMore()
type LoadMoreEvent = { target: { complete: () => void } };
export const createLoadMoreEvent = (onComplete: () => void): LoadMoreEvent => ({
	target: { complete: onComplete },
});

export interface InfiniteBiScrollProps {
	paginationLevel: Ref<PaginationLevel>;
	loading: Ref<boolean>;
	pageSize: Ref<number>;
	titlesAmount: Ref<number>;
}

export function useBiScroll(
	el: Ref<HTMLElement | null>,
	{ loading, pageSize, titlesAmount, paginationLevel }: InfiniteBiScrollProps,
	onEmit: (e: 'load-next' | 'load-prev', payload: { event: LoadMoreEvent; page: number }) => void
) {
	const route = useRoute();
	const router = useRouter();

	const activated = ref(true);
	const initialised = ref(false);
	const topOffset = ref(0);
	const page = ref(getPageQueryParam(route.query));
	const pageStart = ref(page.value); // lowest page query param

	// FYI the back end has a hardcoded value of 2000 titles
	const hardPageLimit = computed(() =>
		paginationLevel.value === 0 ? 0 : PAGINATION_LEVELS[paginationLevel.value].pages
	);
	const lastPage = computed(() => {
		if (!titlesAmount.value) return hardPageLimit.value;
		const last = Math.ceil(titlesAmount.value / pageSize.value);
		return last > hardPageLimit.value ? hardPageLimit.value : last;
	});
	const loadingPrev = ref(false);
	const loadingNext = ref(page < lastPage);
	const anyLoading = computed<boolean>(() => loading.value || loadingPrev.value || loadingNext.value);
	const enabled = computed(() => paginationLevel.value > 0);

	// upage `page` query param as with window.replace, so it doesn't add a history state.
	// the user will scroll from up and down, load new titles on top and bottom. in all these cases,
	// the `page` param will update accordingly.
	function replacePageQueryParam() {
		if (page.value > 1) {
			if (route.query.path !== page.value + '') {
				// update page parameter (ignores navigation duplication error)
				router.replace({ path: route.path, query: { ...route.query, page: page.value + '' } }).catch(() => {});
			}
		} else if (page.value <= 1) {
			if (route.query.page) {
				// remove page parameter
				// eslint-disable-next-line @typescript-eslint/no-unused-vars
				const { page, ...query } = route.query;
				router.replace({ path: route.path, query }).catch(() => {});
			}
		}
	}

	// find the lowest page.
	// since we can load more content in both direction, it's important know where the page number starts,
	// so the anchor IDs can be calculated accordingly.
	function updateTheLowestPage() {
		const lowest = Math.min(page.value, pageStart.value);
		if (lowest < pageStart.value) {
			pageStart.value = lowest;
		}
	}

	function getAnchorPositions(): number[] {
		const anchors = el.value?.querySelectorAll(`[data-page="${ANCHOR_NAME}"]`) ?? [];
		return [...anchors].map(item => item.getBoundingClientRect().top + window.scrollY);
	}

	function updatePageParam(scrollY: number) {
		// it's possible that enabled changes during filtering (with query parameters or without)
		if (!enabled.value) {
			return;
		}

		// there is a possibility that the mounting happens before the useResizeObserver calculates.
		if (!initialised.value || !activated.value) {
			return;
		}
		const position = scrollY + window.innerHeight / 2 + topOffset.value;
		// Query anchor positions fresh from the DOM so that layout changes outside `el`
		// (e.g. the GuideModuleLoader being inserted above the timeline) are always reflected.
		const activePage = getAnchorPositions().reduce((root, value, index) => {
			return value < position ? pageStart.value - 1 + index : root;
		}, 0);
		// update current page
		if (page.value != activePage + 1) {
			page.value = activePage + 1;

			updateTheLowestPage();
			if (page.value <= lastPage.value) {
				// never update query parameter above lastPage.
				// but infinite scroll and loading continues nevertheless, just without updating query parameters.
				replacePageQueryParam();
			}
		}
	}

	onMounted(() => {
		// if page parameter is used, scroll into view so the user can scroll up to `loadLess()` titles.
		if (page.value > 1 && enabled.value) {
			window.scrollBy(0, 20);
		}
		const scroll = useScroll(document, {
			throttle: 100,
			behavior: 'smooth',
			offset: { bottom: getThresholdValue(getViewport()) },
		});

		function triggerLoadNext() {
			loadingNext.value = true;
			const onComplete = () => {
				loadingNext.value = false;
				// If we're on page > 1 and still at scrollY = 0, the initial scrollBy(0, 20) on
				// mount failed because the content fit in the viewport. Now that content has grown,
				// retry so the user has room to scroll up and trigger load-prev.
				if (
					page.value > 1 &&
					scroll.y.value === 0 &&
					document.documentElement.scrollHeight > window.innerHeight
				) {
					window.scrollBy(0, 20);
				}
			};
			const event = createLoadMoreEvent(onComplete);
			onEmit('load-next', { event, page: page.value + 1 });
		}

		// infinite scroll implementation by using useScroll.
		// because useInfiniteScroll seems to be buggy in many versions of @vueuse.
		watch(scroll.arrivedState, state => {
			// it's possible that enabled changes during filtering (with query parameters or without)
			if (!enabled.value) {
				return;
			}

			if (anyLoading.value || !activated.value) {
				return;
			}

			if (state.bottom) {
				triggerLoadNext();
			}
		});

		// If the user scrolls to the bottom while a load is in progress, arrivedState.bottom fires
		// but is blocked by anyLoading. When loading finishes, arrivedState.bottom won't re-fire
		// since it hasn't changed. Retry here so fast scrollers aren't left stuck at the bottom.
		watch(anyLoading, isLoading => {
			if (isLoading || !enabled.value || !activated.value) return;
			if (scroll.arrivedState.bottom) {
				triggerLoadNext();
			}
		});

		// Load prev only when the user has actively scrolled back to the top (oldY > 0 → newY <= 0).
		// Using arrivedState.top would fire immediately on large screens where the initial content
		// fits within the viewport and scrollY never leaves 0.
		watch(scroll.y, (newY, oldY = 0) => {
			if (enabled.value && !anyLoading.value && activated.value && page.value !== 1 && newY <= 0 && oldY > 0) {
				loadingPrev.value = true;
				const { height } = el.value?.getBoundingClientRect()!;
				const onComplete = () => {
					pageStart.value = page.value - 1;
					loadingPrev.value = false;
					// restore scroll position because content is prepended
					const { height: newHeight } = el.value?.getBoundingClientRect()!;
					const withPlaceholderDiv = page.value === 1 ? 20 : 0;
					window.scrollBy(0, window.scrollY + (newHeight - height) - withPlaceholderDiv);
				};
				const event = createLoadMoreEvent(onComplete);
				onEmit('load-prev', { event, page: page.value - 1 });
			}
			updatePageParam(newY);
		});

		useResizeObserver(el, () => {
			if (!activated.value || !enabled.value) {
				return;
			}

			const topPagePlacement = document?.querySelector(`[data-page="${BI_SCROLL_TOP_OFFSET_PLACEMENT_ID}"]`)!;

			if (topPagePlacement) {
				const rect = topPagePlacement.getBoundingClientRect();
				topOffset.value = rect.height;
			}

			if (!initialised.value) {
				initialised.value = true;
			}
		});
	});

	// make sure we don't update query params while different views are active
	onActivated(() => {
		activated.value = true;
	});
	onDeactivated(() => {
		activated.value = false;
	});

	return {
		page,
		lastPage,
		pageStart: readonly(pageStart),
		loadingPrev: readonly(loadingPrev),
		loadingNext: readonly(loadingNext),
		anyLoading,
		ANCHOR_NAME,
	};
}
